import { Theme } from '@mui/material/styles';

/**
 * Centralized UI component theming configuration
 * Provides consistent colors and styles for buttons, calendars, and form elements
 */

export interface UIThemeConfig {
  buttons: {
    contained: {
      backgroundColor: string;
      color: string;
      hoverBackgroundColor: string;
      disabledBackgroundColor: string;
      disabledColor: string;
    };
    outlined: {
      color: string;
      borderColor: string;
      hoverColor: string;
      hoverBorderColor: string;
      disabledColor: string;
      disabledBorderColor: string;
    };
    text: {
      color: string;
      hoverBackgroundColor: string;
      hoverColor: string;
      disabledColor: string;
    };
  };
  datePicker: {
    borderColor: string;
    hoverBorderColor: string;
    focusBorderColor: string;
    focusBoxShadow: string;
    labelColor: string;
    focusLabelColor: string;
    inputColor: string;
  };
}

/**
 * Generate UI theme configuration based on MUI theme
 */
export const getUIThemeConfig = (theme: Theme): UIThemeConfig => {
  const isDark = theme.palette.mode === 'dark';
  const buttonMain = theme.palette.primary.main;
  const outlinedColor = theme.palette.primary.main;

  return {
  buttons: {
    contained: {
      backgroundColor: buttonMain,
      color: theme.palette.primary.contrastText,
      hoverBackgroundColor: buttonMain,
      disabledBackgroundColor: (theme.palette as any).button?.disabled || theme.palette.action.disabledBackground,
      disabledColor: theme.palette.action.disabled,
    },
    outlined: {
      color: outlinedColor,
      borderColor: outlinedColor,
      hoverColor: outlinedColor,
      hoverBorderColor: outlinedColor,
      disabledColor: theme.palette.action.disabled,
      disabledBorderColor: theme.palette.action.disabled,
    },
    text: {
      // Dark: primary.main (#1B8187) fails on dark surfaces; match outlined text contrast.
      color: isDark ? outlinedColor : theme.palette.secondary.main,
      hoverBackgroundColor: isDark ? `rgba(${theme.palette.primary.mainChannel} / 0.12)` : theme.palette.secondary.main,
      hoverColor: isDark ? outlinedColor : theme.palette.secondary.contrastText,
      disabledColor: theme.palette.action.disabled,
    },
  },
  datePicker: {
    borderColor: theme.palette.divider,
    hoverBorderColor: theme.palette.primary.main,
    focusBorderColor: theme.palette.primary.main,
    focusBoxShadow: `0 0 0 2px ${theme.palette.primary.main}25`,
    labelColor: theme.palette.text.primary,
    focusLabelColor: theme.palette.primary.main,
    inputColor: theme.palette.text.primary,
  },
  };
};

/**
 * Hook to get UI theme configuration
 */
export const useUIThemeConfig = () => {
  const theme = require('@mui/material/styles').useTheme();
  return getUIThemeConfig(theme);
};

/**
 * Predefined color schemes for different contexts
 */
export const uiColorSchemes = {
  // Default scheme
  default: getUIThemeConfig,

  // Dark scheme for specific contexts
  dark: (theme: Theme): UIThemeConfig => ({
    buttons: {
      contained: {
        backgroundColor: theme.palette.grey[800],
        color: theme.palette.getContrastText(theme.palette.grey[800]),
        hoverBackgroundColor: theme.palette.grey[700],
        disabledBackgroundColor: theme.palette.grey[600],
        disabledColor: theme.palette.action.disabled,
      },
      outlined: {
        color: theme.palette.grey[300],
        borderColor: theme.palette.grey[300],
        hoverColor: theme.palette.grey[200],
        hoverBorderColor: theme.palette.grey[200],
        disabledColor: theme.palette.action.disabled,
        disabledBorderColor: theme.palette.action.disabled,
      },
      text: {
        color: theme.palette.grey[300],
        hoverBackgroundColor: theme.palette.grey[700],
        hoverColor: theme.palette.getContrastText(theme.palette.grey[700]),
        disabledColor: theme.palette.action.disabled,
      },
    },
    datePicker: {
      borderColor: theme.palette.grey[600],
      hoverBorderColor: theme.palette.grey[400],
      focusBorderColor: theme.palette.primary.main,
      focusBoxShadow: `0 0 0 2px ${theme.palette.primary.main}25`,
      labelColor: theme.palette.grey[400],
      focusLabelColor: theme.palette.primary.main,
      inputColor: theme.palette.grey[100],
    },
  }),

  // Success-focused scheme
  success: (theme: Theme): UIThemeConfig => ({
    buttons: {
      contained: {
        backgroundColor: theme.palette.success.main,
        color: theme.palette.success.contrastText,
        hoverBackgroundColor: theme.palette.success.dark,
        disabledBackgroundColor: theme.palette.success.light,
        disabledColor: theme.palette.action.disabled,
      },
      outlined: {
        color: theme.palette.success.main,
        borderColor: theme.palette.success.main,
        hoverColor: theme.palette.success.dark,
        hoverBorderColor: theme.palette.success.dark,
        disabledColor: theme.palette.action.disabled,
        disabledBorderColor: theme.palette.action.disabled,
      },
      text: {
        color: theme.palette.success.main,
        hoverBackgroundColor: theme.palette.success.main,
        hoverColor: theme.palette.success.contrastText,
        disabledColor: theme.palette.action.disabled,
      },
    },
    datePicker: {
      borderColor: theme.palette.divider,
      hoverBorderColor: theme.palette.success.main,
      focusBorderColor: theme.palette.success.main,
      focusBoxShadow: `0 0 0 2px ${theme.palette.success.main}25`,
      labelColor: theme.palette.text.primary,
      focusLabelColor: theme.palette.success.main,
      inputColor: theme.palette.text.primary,
    },
  }),

  // Warning-focused scheme
  warning: (theme: Theme): UIThemeConfig => ({
    buttons: {
      contained: {
        backgroundColor: theme.palette.warning.main,
        color: theme.palette.warning.contrastText,
        hoverBackgroundColor: theme.palette.warning.dark,
        disabledBackgroundColor: theme.palette.warning.light,
        disabledColor: theme.palette.action.disabled,
      },
      outlined: {
        color: theme.palette.warning.main,
        borderColor: theme.palette.warning.main,
        hoverColor: theme.palette.warning.dark,
        hoverBorderColor: theme.palette.warning.dark,
        disabledColor: theme.palette.action.disabled,
        disabledBorderColor: theme.palette.action.disabled,
      },
      text: {
        color: theme.palette.warning.main,
        hoverBackgroundColor: theme.palette.warning.main,
        hoverColor: theme.palette.warning.contrastText,
        disabledColor: theme.palette.action.disabled,
      },
    },
    datePicker: {
      borderColor: theme.palette.divider,
      hoverBorderColor: theme.palette.warning.main,
      focusBorderColor: theme.palette.warning.main,
      focusBoxShadow: `0 0 0 2px ${theme.palette.warning.main}25`,
      labelColor: theme.palette.text.primary,
      focusLabelColor: theme.palette.warning.main,
      inputColor: theme.palette.text.primary,
    },
  }),
};

/**
 * Get UI theme configuration by scheme
 */
export const getUIThemeConfigByScheme = (theme: Theme, scheme: keyof typeof uiColorSchemes = 'default') => {
  return uiColorSchemes[scheme](theme);
};

/**
 * Hook to get UI theme configuration by scheme
 */
export const useUIThemeConfigByScheme = (scheme: keyof typeof uiColorSchemes = 'default') => {
  const theme = require('@mui/material/styles').useTheme();
  return uiColorSchemes[scheme](theme);
};
